'use client'

import type { ReactNode } from 'react'

import {
  OrderCardMenu,
  OrderChannelBadges,
  type OrderCardFact,
  type OrderCarrier,
} from '@/components/orders/OrderCardChrome'
import { OrderCardPhoto, useProductPrimaryPhoto } from '@/components/orders/OrderCardPhoto'
import { resolveStorefront } from '@/components/warehouse/storefronts'
import { LocalDateTime } from '@/components/ui/LocalDateTime'
import type { MissionControlStage } from '@/lib/order-mission-control'
import type { YMarketItem, YMarketOrder } from '@/lib/types'

const STAGE_ICON_PATHS: Record<MissionControlStage, ReactNode> = {
  delivery_issue: <><path d="M12 4 2.5 20h19z" /><path d="M12 10v4.5M12 17.2v.3" /></>,
  returning: <><path d="M9 6 4 11l5 5" /><path d="M4 11h10a5 5 0 0 1 0 10h-3" /></>,
  out_for_delivery: <><path d="M6 7h9v9H6" /><path d="M15 10h3.5l2.5 3v3h-6" /><circle cx="9" cy="17.5" r="1.7" /><circle cx="18" cy="17.5" r="1.7" /><path d="M1.5 10h3M2.5 13h2" /></>,
  ready_for_pickup: <><path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" /><circle cx="12" cy="11" r="2.2" /></>,
  in_transit: <><path d="M3 6h11v10H3z" /><path d="M14 9h4l3 3v4h-7" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="17" cy="17.5" r="1.8" /></>,
  preparing: <><path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5z" /><path d="M3.5 7.5 12 12l8.5-4.5M12 12v9" /></>,
  processing: <><path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5z" /><path d="M3.5 7.5 12 12l8.5-4.5M12 12v9" /></>,
  delivery_pending: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  awaiting_payment: <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M7 15h3" /></>,
}

export function StageIcon({ stage }: { stage: MissionControlStage }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-stage-icon={stage} className="shrink-0">
      {STAGE_ICON_PATHS[stage]}
    </svg>
  )
}

function orderCarrier(order: YMarketOrder): OrderCarrier {
  // DBS parcels leave through CDEK; every other Market programme is carried by Yandex.
  return (order.programType || 'DBS') === 'DBS' ? 'cdek' : 'yandex'
}

function lineLabel(line: YMarketItem, multiple: boolean, label: string): string {
  return multiple && line.offerName ? `${line.offerName}: ${label}` : label
}

function lineFact(
  line: YMarketItem,
  multiple: boolean,
  label: string,
  value: string | null | undefined,
): OrderCardFact[] {
  return value ? [{ label: lineLabel(line, multiple, label), value }] : []
}

function storefrontLabel(providerCode: string): string {
  const resolved = resolveStorefront(providerCode === 'yandex_market' ? 'ymarket' : providerCode)
  return resolved.kind === 'known' ? resolved.entry.label : providerCode
}

function lineIdentifierFacts(line: YMarketItem, multiple: boolean): OrderCardFact[] {
  const facts: OrderCardFact[] = [
    ...lineFact(line, multiple, 'Артикул продавца (Маркет)', line.offerId),
    ...(line.instances ?? []).flatMap((instance) => [
      ...lineFact(line, multiple, 'УИН (Маркет)', instance.uin),
      ...lineFact(line, multiple, 'Код маркировки (Маркет)', instance.cis),
    ]),
  ]
  if (line.productId && line.productIdentifiers) {
    const identifiers = line.productIdentifiers
    facts.push(
      ...lineFact(line, multiple, 'Артикул', identifiers.article),
      ...lineFact(line, multiple, 'УИН', identifiers.uin),
      ...lineFact(line, multiple, 'Размер', identifiers.size),
      ...(identifiers.sellerSkus ?? []).flatMap((sellerSku) => (
        sellerSku.providerCode === 'ymarket' || sellerSku.providerCode === 'yandex_market'
          ? []
          : lineFact(
              line,
              multiple,
              `Артикул продавца (${storefrontLabel(sellerSku.providerCode)})`,
              sellerSku.value,
            )
      )),
      ...(identifiers.aliases ?? []).flatMap((alias) => (
        alias.kind === 'vendor_code'
          ? lineFact(line, multiple, 'Код поставщика (Wildberries)', alias.value)
          : []
      )),
    )
  } else if (!line.productId) {
    facts.push({ label: lineLabel(line, multiple, 'Позиция не связана с изделием каталога') })
  }
  return facts
}

function orderFacts(order: YMarketOrder, waybill: string): OrderCardFact[] {
  const items = order.items ?? []
  const multiple = items.length > 1
  return [
    { label: 'Номер заказа', value: String(order.orderId) },
    ...(waybill ? [{ label: 'Накладная', value: waybill }] : []),
    ...items.flatMap((line) => lineIdentifierFacts(line, multiple)),
  ]
}

export interface OrderCardDetails {
  place?: string | null
  createdAt: string
  /** The provider's own status wording, when it says more than the stage label. */
  note?: string | null
  timing?: string | null
}

const DETAIL_SLOTS = 3

interface DetailLine {
  key: string
  content: ReactNode
  accent?: boolean
}

/**
 * Fills the panel's three fixed slots in reading order. The order number and date share
 * a line only when every slot is taken; a missing fact hands its slot to them instead,
 * so the panel never shows a hole and never changes height.
 */
function detailLines(orderId: number, { place, createdAt, note, timing }: OrderCardDetails): DetailLine[] {
  const facts: Record<'place' | 'note' | 'timing', DetailLine | null> = {
    place: place ? { key: 'place', content: place } : null,
    note: note ? { key: 'note', content: note } : null,
    timing: timing ? { key: 'timing', content: timing, accent: true } : null,
  }
  // With all three present the provider note yields; it stays in the stage label's tooltip.
  if (facts.place && facts.note && facts.timing) facts.note = null
  const optional = [facts.place, facts.note, facts.timing].filter((line): line is DetailLine => line !== null)
  const number = <span className="tab-nums">№{orderId}</span>
  const identity: DetailLine[] = DETAIL_SLOTS - optional.length >= 2
    ? [
        { key: 'number', content: number },
        { key: 'created', content: <LocalDateTime value={createdAt} prefix="Оформлен " /> },
      ]
    : [{ key: 'number', content: <>{number} · <LocalDateTime value={createdAt} /></> }]
  return [
    ...(facts.place ? [facts.place] : []),
    ...identity,
    ...(facts.note ? [facts.note] : []),
    ...(facts.timing ? [facts.timing] : []),
  ]
}

export interface OrderCardProps {
  order: YMarketOrder
  stage: MissionControlStage
  /** Text colour class for the stage icon and label. */
  toneClass: string
  stateLabel: string
  stateDetail?: string
  amountLabel: string
  details: OrderCardDetails
  waybill?: string
  alert?: boolean
  packed?: boolean
  onOpen: () => void
  /** A floating icon button beside the menu, shown only when the action is possible. */
  action?: ReactNode
}

/**
 * One order as a photo card: the piece on top, a frosted panel with who and what,
 * the whole face opening the order, and identifiers behind the ⋯ menu.
 */
export function OrderCard({
  order,
  stage,
  toneClass,
  stateLabel,
  stateDetail,
  amountLabel,
  details,
  waybill = '',
  alert = false,
  packed = false,
  onOpen,
  action = null,
}: OrderCardProps) {
  const lead = order.items?.[0]
  const product = lead?.offerName || lead?.offerId || 'Заказ без названия товара'
  const extraItems = Math.max(0, (order.items?.length ?? 0) - 1)
  const cancellationRequest = order.buyerCancellationRequest
  // The first line's own piece only: a later line's photo would picture a different piece.
  const { objectUrl: photo } = useProductPrimaryPhoto(lead)

  return (
    <article
      className={`group relative isolate flex aspect-[4/3] min-w-0 flex-col justify-end rounded-lg border bg-paper transition-shadow hover:shadow-[0_6px_20px_-12px_rgba(60,40,10,0.35)] focus-within:z-20 has-[[aria-expanded=true]]:z-30 motion-reduce:transition-none sm:aspect-[4/5] ${alert ? 'border-st-unpaid/45' : 'border-rule'}`}
    >
      <OrderCardPhoto objectUrl={photo} />
      {/* Inert except its chips and buttons, so the rest of the strip still opens the order. */}
      <div className="pointer-events-none absolute inset-x-2 top-2 z-10 flex items-start justify-between gap-1.5">
        <OrderChannelBadges carrier={orderCarrier(order)} />
        <div className="flex shrink-0 items-center gap-1.5">
          <OrderCardMenu orderId={order.orderId} facts={orderFacts(order, waybill)} onOpen={onOpen} />
          {action}
        </div>
      </div>
      <div className="grid min-w-0">
        {/* The frost is a childless sibling: a backdrop-filter ancestor would trap the stretched open button in the panel. */}
        <div aria-hidden="true" className="-mt-7 rounded-b-lg bg-paper/45 backdrop-blur-[3px] [grid-area:1/1] [mask-image:linear-gradient(to_bottom,transparent,#000_1.75rem)]" />
        {/* isolate, not relative: it must paint above the frost's stacking context without becoming the open button's containing block. */}
        <div className="isolate flex min-w-0 flex-col gap-1 px-3 pb-2.5 pt-1 [grid-area:1/1]">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <div className={`flex min-w-0 items-center gap-1.5 ${toneClass}`} title={stateDetail}>
              <StageIcon stage={stage} />
              <span className="truncate text-[10px] font-semibold">{stateLabel}</span>
              {packed ? (
                <span className="shrink-0 rounded border border-st-delivered/40 bg-st-delivered/10 px-1 text-[10px] font-semibold text-st-delivered">
                  Упакован
                </span>
              ) : null}
            </div>
            <span className="shrink-0 text-[12px] font-semibold tab-nums text-ink">{amountLabel}</span>
          </div>
          {cancellationRequest ? (
            <div
              role="status"
              data-testid="buyer-cancellation-request"
              className="rounded border border-st-unpaid/35 bg-st-unpaid/10 px-1.5 py-1 text-[10px] font-semibold leading-snug text-st-unpaid"
            >
              Покупатель просит отменить заказ
              {cancellationRequest.deadlineAt ? (
                <span className="font-medium"> · <LocalDateTime value={cancellationRequest.deadlineAt} prefix="ответить до " /></span>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            onClick={onOpen}
            aria-label={`Открыть заказ №${order.orderId}`}
            title={product}
            className="min-w-0 truncate text-left text-[13px] font-semibold leading-snug text-ink after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-gold group-hover:text-gold-deep"
          >
            {product}{extraItems > 0 ? <span className="font-normal text-ink-faint"> +{extraItems}</span> : null}
          </button>
          {/* Lines pack from the top; a short set leaves its spare slot below, never between title and facts. */}
          <div className="grid min-w-0 grid-rows-3 gap-0.5 text-[10px] leading-snug text-ink-muted">
            {detailLines(order.orderId, details).map((line) => (
              <span key={line.key} className={`min-w-0 truncate ${line.accent ? 'font-medium text-gold-deep' : ''}`} title={typeof line.content === 'string' ? line.content : undefined}>
                {line.content}
              </span>
            ))}
          </div>
        </div>
      </div>
    </article>
  )
}
